<template>
    <div>
        <!-- 🔄 GLOBAL LOADING OVERLAY SPINNER -->
        <v-overlay :value="loading" opacity="0.6" z-index="9999">
            <div class="d-flex flex-column align-center justify-center">
                <v-progress-circular indeterminate size="70" width="7" color="#003366" class="mb-4" />
                <div class="loading-caption">Calculating... please wait!</div>
            </div>
        </v-overlay>

        <v-row dense class="px-0 pt-0 secondary">
            <!-- Site Details -->
            <v-col cols="4" class="px-8 py-5 d-flex flex-column justify-center">
                <div class="text-h6 font-weight-light white--text">
                    {{ site?.orgName }} | <strong>{{ site?.siteName }}</strong>
                </div>
                <div class="text-body-1 font-weight-normal white--text mt-2">
                    {{ site?.addressLine1 }} {{ site?.addressLine2 }} {{ site?.town }} {{ site?.county }} {{
                        site?.postcode }}
                </div>
            </v-col>
            <v-col cols="4" class="px-8 d-flex py-5 align-end justify-center">
                <div class="d-flex">
                    <VBtn class="primary mr-1"
                        @click.native="MIX_goExternal('/site-reports/' + site.id + '?startDate=' + startDate + '&endDate=' + endDate)">
                        <v-icon class="mr-2"> icons8-export-pdf</v-icon>PDF
                    </VBtn>
                    <VBtn @click.native="exportLoadCSV" class="primary mx-1"><v-icon
                            class="mr-2">icons8-csv</v-icon>Load</VBtn>
                    <VBtn @click.native="exportUsageCSV" class="primary mx-1"><v-icon
                            class="mr-2">icons8-csv</v-icon>Usage</VBtn>
                </div>
            </v-col>
            <v-col cols="4" class="d-flex justify-end align-center px-5">
                <!-- Date Fields & Shortcut Buttons-->
                <div class="pa-4">
                    <!-- Date Picker-->
                    <div class="d-flex justify-space-between">
                        <!-- StartDate -->
                        <div style="width: 50%" class="pr-3">
                            <!--                                :min="site.startDateTime ? this.$dayjs(site.startDateTime).format('YYYY-MM-DD') : '2021-01-01'"-->
                            <!--                                :max="this.$dayjs().format('YYYY-MM-DD')"-->
                            <DatePicker BackgroundColor="white" label="Start Date" v-model="startDate" :dense="true"
                                :clearable="false" />
                        </div>
                        <!-- EndDate -->
                        <div style="width: 50%">
                            <DatePicker BackgroundColor="white" label="End Date" v-model="endDate" :clearable="false"
                                :dense="true" />
                        </div>
                    </div>
                    <!-- Last Week, Last Month, Last Quarter, Last Year-->
                    <div>
                        <div class="d-flex mt-4">
                            <div class="d-flex">
                                <v-icon class="mx-2 text-h4" @click="day = !day"
                                    :class="day ? 'white--text' : 'grey--text'">icons8-sun</v-icon>
                                <v-icon class="mx-2 mr-5 text-h4" @click="night = !night"
                                    :class="night ? 'white--text' : 'grey--text'">icons8-moon-and-stars</v-icon>
                            </div>

                            <VBtn @click.native="setDateField('week')" class="primary mr-1">Last 7 Days</VBtn>
                            <VBtn @click.native="setDateField('month')" class="primary mx-1">Last 30 Days</VBtn>
                            <VBtn @click.native="setDateField('quarter')" class="primary mx-1">Last 90 Days</VBtn>
                            <VBtn @click.native="calculate" class="primary mx-1"><v-icon
                                    class="mr-2">icons8-refresh</v-icon>Update</VBtn>
                        </div>
                    </div>
                </div>
            </v-col>
        </v-row>
        <!-- ESTIMATED -->
        <v-row dense class="px-5 pt-2">
            <v-col v-if="showHidden > 10" cols="2">
                <DashCard @click.native="showHidden--" title="Estimated Usage" icon="icons8-speed"
                    :value="estimateTotal_kWh.toFixed(2)" unit="kWh"
                    :description="day && night ? 'Power Used' : 'Power Used (Filtered)'" :loading="loading" />
            </v-col>
            <!-- Estimated Diesel Usage -->
            <v-col v-if="showHidden > 10 && site?.type === 'Diesel'" cols="2">
                <DashCard title="Estimated Diesel Usage" icon="icons8-engine" :value="estimateTotal_Diesel" unit="ltr"
                    description="Diesel Used" :loading="loading" />
            </v-col>
            <!-- Estimated Carbon Footprint -->
            <v-col v-if="showHidden > 10" cols="2">
                <DashCard title="Estimated Carbon Footprint" icon="icons8-co2" :value="estimateTotal_CO2" unit="kg"
                    description="C02 Emitted" :loading="loading" />
            </v-col>
            <!-- Estimated Total Cost -->
            <v-col v-if="showHidden > 10" cols="2">
                <DashCard title="Estimated Total Cost" icon="icons8-sales-performance" :value="estimateTotal_Cost"
                    unit="£" description="Spent" :loading="loading" />
            </v-col>
        </v-row>
        <!-- USAGE -->
        <v-row dense class="px-5 pt-2">
            <!-- Total Power Usage -->
            <v-col cols="2">
                <v-tooltip bottom v-if="day !== night">
                    <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on" @click="showHidden++">
                            <DashCard title="Total Usage" icon="icons8-speed"
                                :value="usageTotal_kWh < 0 ? 0 : usageTotal_kWh" unit="kWh"
                                :description="'Power Used (Aggregated - Filtered)'" :loading="loading" />
                        </div>
                    </template>
                    <span>
                        This total may differ slightly from the full-period value due to rounding when filtering by day
                        or night. Variance
                        is typically less than 0.3%.
                    </span>
                </v-tooltip>

                <!-- fallback card -->
                <DashCard v-else @click.native="showHidden++" title="Total Usage" icon="icons8-speed"
                    :value="usageTotal_kWh < 0 ? 0 : usageTotal_kWh" unit="kWh"
                    :description="site?.useMainMeter == 1 ? 'Power Used (Main Meter)' : 'Power Used (Aggregated)'"
                    :loading="loading" />
            </v-col>

            <!-- Accommodation Usage -->
            <v-col cols="2">
                <v-tooltip bottom v-if="day !== night">
                    <template v-slot:activator="{ on, attrs }">
                        <div v-bind="attrs" v-on="on">
                            <DashCard title="Accommodation Usage" :value="accommodationUsageTotal_kWh"
                                icon="icons8-royal-tent" unit="kWh" description="Power Used (Filtered)"
                                :loading="loading" />
                        </div>
                    </template>
                    <span>
                        This total may differ slightly from the full-period value due to rounding when filtering by day
                        or night. Variance is typically less than 0.3%.
                    </span>
                </v-tooltip>

                <DashCard v-else title="Accommodation Usage" :value="accommodationUsageTotal_kWh"
                    icon="icons8-royal-tent" unit="kWh" description="Power Used" :loading="loading" />
            </v-col>
            <!-- Diesel Usage-->
            <v-col cols="2" v-if="site?.type === 'Diesel'">
                <DashCard title="Calculated Diesel Usage" icon="icons8-engine"
                    :value="usageTotal_Diesel < 0 ? 0 : usageTotal_Diesel" unit="ltr" description="Diesel Used"
                    :loading="loading" />
            </v-col>
            <!--Carbon Footprint-->
            <v-col cols="2">
                <DashCard title="Carbon Footprint" icon="icons8-co2" :value="usageTotal_CO2 < 0 ? 0 : usageTotal_CO2"
                    unit="kg" description="of CO2" :loading="loading" />
            </v-col>
            <!--Total Cost-->
            <v-col cols="2">
                <DashCard title="Total Cost" icon="icons8-sales-performance"
                    :value="usageTotal_Cost < 0 ? 0 : usageTotal_Cost" unit="£" description="Spent"
                    :loading="loading" />
            </v-col>
            <!-- kWh Per M2 -->
            <!--            <v-col cols="2">-->
            <!--                <DashCard title="kWh Per m2" icon="icons8-electrical-threshold" :value="0" unit="kWh" description="Per m2" :loading="loading" />-->
            <!--            </v-col>-->
            <!-- Occupancy -->
            <v-col cols="2">
                <DashCard title="Occupancy" icon="icons8-eye" :unit="site?.occupancy" description="People"
                    :loading="loading" />
            </v-col>
        </v-row>
        <!-- SAVINGS -->
        <v-row dense class="px-5 pt-2">
            <!-- Renewable Generation (show if site has generators) -->
            <v-col cols="2" v-if="hasRenewableCircuits">
                <DashCard title="Renewable Generation" icon="icons8-solar-panel" :value="generationTotal_kWh" 
                    unit="kWh" description="Generated" :loading="loading" />
            </v-col>
            <!-- Tier (show if no renewable circuits) -->
            <v-col cols="2" v-else>
                <DashCard title="Tier" :value="site?.tier" :loading="loading" />
            </v-col>
            <!-- Energy Saved -->
            <v-col cols="2">
                <DashCard title="Energy Saved" icon="icons8-speed" :value="usageSaving_kWh < 0 ? 0 : usageSaving_kWh"
                    unit="kWh" description="Power Saved" :loading="loading" />
            </v-col>
            <!-- Diesel Saved -->
            <v-col cols="2" v-if="site?.type === 'Diesel'">
                <DashCard title="Diesel Saved" icon="icons8-engine"
                    :value="usageSaving_Diesel < 0 ? 0 : usageSaving_Diesel" unit="ltr" description="Diesel Saved"
                    :loading="loading" />
            </v-col>
            <!-- CO2 Saved -->
            <v-col cols="2">
                <DashCard title="CO2 Saved" icon="icons8-co2" :value="usageSaving_CO2 < 0 ? 0 : usageSaving_CO2"
                    unit="kg" description="CO2 Reduction" :loading="loading" />
            </v-col>
            <!-- Cost Saving -->
            <v-col cols="2">
                <DashCard title="Cost Saving" icon="icons8-sales-performance"
                    :value="usageSaving_Total < 0 ? 0 : usageSaving_Total" unit="£" description="Saved"
                    :loading="loading" />
            </v-col>
            <!-- Net Saving -->
            <v-col cols="2">
                <DashCard title="Net Saving" icon="icons8-sales-performance"
                    :value="day && night ? (usageSaving_NET < 0 ? 0 : usageSaving_NET) : ''"
                    :unit="day && night ? '£' : ''"
                    :description="day && night ? 'Saving' : 'Not shown when filtering by day or night'"
                    :loading="loading" />
            </v-col>
            <!-- Average Load -->
            <!--            <v-col cols="2">-->
            <!--                <DashCard title="Average Load" icon="icons8-voltmeter" :value="computedAverageLoad" unit="kW" description="Load" :loading="loading" />-->
            <!--            </v-col>-->
            <!-- CHARTS -->
            <!--Load (W) - Area Chart -->
            <v-col cols="6" class="mt-2">
                <BasicCard>
                    <template v-slot:header>
                        <div class="full-width primary rounded-t-lg d-flex align-center px-4"
                            style="height: 80px !important; overflow: hidden !important">
                            <div class="text-body-1 font-weight-bold white--text text-left ml-4">Load (W)</div>
                            <v-spacer />
                            <v-icon class="primary--text text--lighten-4 text-h4">icons8-area-chart</v-icon>
                        </div>
                    </template>
                    <DropdownAdvanced v-model="loadCircuit" :items="circuits" itemtext="name" itemvalue="id"
                        label="Select Circuit" />
                    <LineChart :loading="loading" :datadown="loadSeries" :height="400" :key="chartKey" xaxislabel="Time"
                        yaxislabel="Load" yaxisunit="W"></LineChart><br />
                </BasicCard>
            </v-col>
            <!-- Hourly Usage (kWh) - Bar Chart -->
            <v-col cols="6" class="mt-2">
                <BasicCard>
                    <template v-slot:header>
                        <div class="full-width primary rounded-t-lg d-flex align-center px-4"
                            style="height: 80px !important; overflow: hidden !important">
                            <div class="text-body-1 font-weight-bold white--text text-left ml-4">Hourly Usage (kWh)
                            </div>
                            <v-spacer />
                            <v-spacer />
                            <AppButton v-if="computedDays === 0" @click.native="downloadHourlyUsage"
                                class="primary mx-1"><v-icon class="mr-2">icons8 -csv</v-icon>Download Hourly Data
                            </AppButton>
                            <v-icon class="primary--text text--lighten-4 text-h4">icons8-bar-chart-2</v-icon>
                        </div>
                    </template>
                    <DropdownAdvanced v-model="usageRangeCircuit" :items="circuits" itemtext="name" itemvalue="id"
                        label="Select Circuit" />
                    <BarChart2 :loading="loading" :datadown="usageRange" :height="400" class="" xaxislabel="Time"
                        yaxislabel="Usage" yaxisunit="kWh" :key="chartKey"></BarChart2><br />
                </BasicCard>
            </v-col>
            <!-- Top 10 Circuits by Usage - Table -->
            <v-col cols="6" class="mt-2 mb-5">
                <BasicCard>
                    <template v-slot:header>
                        <div class="full-width primary rounded-t-lg d-flex align-center px-4"
                            style="height: 60px !important; overflow: hidden !important">
                            <div class="text-body-1 font-weight-bold white--text text-left">Top 10 Circuits by Usage
                            </div>
                            <v-spacer />
                            <v-icon class="primary--text text--lighten-4 text-h4">icons8-list-2</v-icon>
                        </div>
                    </template>
                    <div v-if="loading" style="height: 400px !important"
                        class="d-flex align-center justify-center flex-column">
                        <v-progress-circular :size="100" :width="10" indeterminate
                            color="grey lighten-3"></v-progress-circular>
                        <div
                            class="grey--text text--lighten-1 mt-1 animate__animated animate__flash animate__slower animate__infinite">
                            Loading</div>
                    </div>
                    <div v-else style="height: 400px !important" class="">
                        <table width="100%" class="pa-5">
                            <thead>
                                <tr>
                                    <th align="left">Name</th>
                                    <th align="left">Group</th>
                                    <th align="left">Type</th>
                                    <th align="right">Usage kWh</th>
                                </tr>
                            </thead>
                            <tr v-for="circuit in computedUsageByCircuit" :key="circuit.id">
                                <td class="body-2">{{ circuit.id }}</td>
                                <td class="body-2">{{ circuit.group }}</td>
                                <td class="text-caption font-weight-bold">
                                    <v-chip label small class="grey lighten-2">{{ circuit?.type?.toUpperCase()
                                    }}</v-chip>
                                </td>
                                <td align="right">{{ circuit?.value.toFixed(2) }}</td>
                            </tr>
                        </table>
                    </div>
                </BasicCard>
            </v-col>
            <!-- Usage by Circuit Type (kWh) - Pie Chart -->
            <v-col cols="6" class="mt-2 mb-5">
                <BasicCard>
                    <template v-slot:header>
                        <div class="full-width primary rounded-t-lg d-flex align-center px-4"
                            style="height: 60px !important; overflow: hidden !important">
                            <div class="text-body-1 font-weight-bold white--text text-left">Usage by Circuit Type (kWh)
                            </div>
                            <v-spacer />
                            <v-icon class="primary--text text--lighten-4 text-h4">icons8-circle-chart</v-icon>
                        </div>
                    </template>
                    <PieChart :loading="loading" :datadown="computedUsageByType" :height="400" :key="chartKey"
                        unit="kWh">
                    </PieChart>
                </BasicCard>
            </v-col>
            <!-- Total Usage (kWh) - Area Chart -->
            <v-col cols="6" class="mt-2">
                <BasicCard>
                    <template v-slot:header>
                        <div class="full-width primary rounded-t-lg d-flex align-center px-4"
                            style="height: 80px !important; overflow: hidden !important">
                            <div class="text-body-1 font-weight-bold white--text text-left ml-4">Total Usage (kWh)</div>
                            <v-spacer />
                            <v-icon class="primary--text text--lighten-4 text-h4">icons8-area-chart</v-icon>
                        </div>
                    </template>
                    <DropdownAdvanced v-model="usageCircuit" :items="circuits" itemtext="name" itemvalue="id"
                        label="Select Circuit" />
                    <!--                    <LineChart-->
                    <!--                        :loading="loading"-->
                    <!--                        :datadown="usageTimeseries"-->
                    <!--                        :height="400"-->
                    <!--                        class=""-->
                    <!--                        xaxislabel="Time"-->
                    <!--                        yaxislabel="Usage"-->
                    <!--                        yaxisunit="kWh"-->
                    <!--                        :key="chartKey"-->
                    <!--                    ></LineChart-->
                    <StackedBarChart xaxislabel="Time" :xaxisdata="usageTimeSeriesXAxis" yaxislabel="Load"
                        yaxisunit="kWh" :isDay="day" :isNight="night" :datadown="usageTimeseries"
                        :dayData="usageTimeseriesDay" :nightData="usageTimeseriesNight" :key="chartKey" :height="400"
                        :loading="loading" />
                    <br />
                </BasicCard>
            </v-col>
            <!-- Hourly Usage (kWh) and Temperature (°C) - Bar / Line Chart -->
            <v-col cols="6" class="mt-2">
                <BasicCard>
                    <template v-slot:header>
                        <div class="full-width primary rounded-t-lg d-flex align-center px-4"
                            style="height: 80px !important; overflow: hidden !important">
                            <div class="text-body-1 font-weight-bold white--text text-left">Hourly Usage (kWh) and
                                Temperature (°C)</div>
                            <v-spacer />
                            <v-icon @click="exportLoadandTempCSV()"
                                class="primary--text text--lighten-4 text-h4">icons8-climate-change</v-icon>
                        </div>
                    </template>
                    <DropdownAdvanced v-model="hourlyUsageCircuit" :items="circuits" itemtext="name" itemvalue="id"
                        label="Select Circuit" />
                    <!--                    <pre>{{ usageByHour }}</pre>-->
                    <BarChart :loading="loading" :xaxisdata="usageByHourXAxis" :yaxisdata="usageByHourYAxis"
                        :y2axisdata="tempByHourYAxis" :datadown="dataByHour" :height="425" class="" xaxislabel="Time"
                        yaxislabel="Usage" y2axislabel="Temperature" yaxisunit="kWh" y2axisunit="°C" :key="chartKey">
                    </BarChart>
                </BasicCard>
            </v-col>
            <!-- Top Groups / Cabins by Usage - Table -->
            <v-col cols="6" class="mt-2 mb-5">
                <BasicCard style="height: 550px !important; overflow: auto !important">
                    <template v-slot:header>
                        <div class="full-width primary rounded-t-lg d-flex align-center px-4"
                            style="height: 80px !important; overflow: hidden !important">
                            <div class="text-body-1 font-weight-bold white--text text-left">
                                Cabin Group Total Usage to {{ $dayjs(endDateUnix).format('DD-MMM-YY') }}
                            </div>
                            <v-spacer />
                            <v-icon class="primary--text text--lighten-4 text-h4">icons8-map-2</v-icon>
                        </div>
                    </template>
                    <!--                    <div>-->
                    <!--                        <pre>{{ computedUsageByGroup }}</pre>-->
                    <!--                    </div>-->
                    <div v-if="loading" style="height: 400px !important"
                        class="d-flex align-center justify-center flex-column">
                        <v-progress-circular :size="100" :width="10" indeterminate
                            color="grey lighten-3"></v-progress-circular>
                        <div
                            class="grey--text text--lighten-1 mt-1 animate__animated animate__flash animate__slower animate__infinite">
                            Loading</div>
                    </div>
                    <div v-else style="height: 400px !important" class="">
                        <table width="100%" class="pa-5">
                            <thead>
                                <tr>
                                    <th align="left">Name</th>
                                    <th align="right">Usage kWh</th>
                                </tr>
                            </thead>
                            <tr v-for="circuit in computedUsageByGroup" :key="circuit.id">
                                <td class="text-h7">{{ circuit.name }}</td>
                                <td class="text-h7" align="right">{{ circuit?.value.toFixed(2) }}</td>
                            </tr>
                        </table>
                    </div>
                </BasicCard>
            </v-col>
            <!-- Top Groups / Cabins by Usage - Table -->
            <v-col cols="6" class="mt-2 mb-5">
                <BasicCard style="height: 550px !important; overflow: auto !important">
                    <template v-slot:header>
                        <div class="full-width primary rounded-t-lg d-flex align-center px-4"
                            style="height: 80px !important; overflow: hidden !important">
                            <div class="text-body-1 font-weight-bold white--text text-left">Cabin Total Usage to {{
                                $dayjs(endDateUnix).format('DD-MMM-YY') }}</div>
                            <v-spacer />
                            <v-icon class="primary--text text--lighten-4 text-h4">icons8-map-2</v-icon>
                        </div>
                    </template>
                    <div v-if="loading" style="height: 400px !important"
                        class="d-flex align-center justify-center flex-column">
                        <v-progress-circular :size="100" :width="10" indeterminate
                            color="grey lighten-3"></v-progress-circular>
                        <div
                            class="grey--text text--lighten-1 mt-1 animate__animated animate__flash animate__slower animate__infinite">
                            Loading</div>
                    </div>
                    <div v-else style="height: 400px !important" class="">
                        <table width="100%" class="pa-5">
                            <thead>
                                <tr>
                                    <th align="left">Name</th>
                                    <th align="right">Usage kWh</th>
                                </tr>
                            </thead>
                            <tr v-for="(location, key) in computedBox" :key="key">
                                <td style="border-bottom: 1px solid lightgrey !important" class="text-h7">{{ key }} - {{
                                    location }}</td>
                                <td style="border-bottom: 1px solid lightgrey !important" class="text-h7" align="right">
                                    {{
                                        getUsageByBox(key) }}</td>
                            </tr>
                        </table>
                    </div>
                </BasicCard>
            </v-col>
        </v-row>
    </div>
</template>

<script>
export default {
    name: 'SiteDashboardViewV3',
    data(vm) {
        return {
            selectedSiteCircuits: [],
            accommodationCircuits: [],
            day: true,
            night: true,
            showHidden: 0,
            site: null,
            loading: false,
            chartKey: 0,
            startDate: null,
            endDate: null,
            initStartDate: vm.$dayjs().subtract(8, 'day').format('YYYY-MM-DD'),
            initEndDate: vm.$dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
            startDateUnix: 0,
            endDateUnix: 0,
            loadSeries: [],
            usageTimeseries: [],
            usageTimeseriesDay: [],
            usageTimeseriesNight: [],
            usageTimeSeriesXAxis: [],
            cabinTempByHour: [],
            cabinHumidityByHour: [],
            cabinOccupancyByHour: [],
            usage_kWh: {
                firstValue: 0,
                lastValue: 0
            },
            stat: {
                powerUsage: 11,
                carbonFootprint: 5,
                totalGridCost: 10,
                powerSaved: 603,
                carbonFootprintGrid: 210,
                totalCostSavingGrid: 804
            },
            totalWLoadX: ['00:00', '01:00', '02:00', '03:00', '04:00', '05:00', '06:00', '07:00', '08:00', '09:00', '10:00', '11:00', '12:00'],
            totalWLoadY: [200, 100, 1200, 300, 400, 500, 600, 700, 800, 900, 1000, 1100, 1200],
            totalWUsageX: ['Usage without Automate', 'Usage with Automate'],
            totalWUsageY: [900, 400],
            // CONVERSIONS
            conversion: {
                kWhPerLitre: '2.3', // per day
                kWhToDiesel: '0.4347826087',
                kWhToGrid: '0.33',
                //fuelCostPerLitre: "0.65", // £ cost per litre
                dieselLtr: '152', // litres per day
                co2Kg: '2.68',
                co2KgHvo: '0.195',
                co2KgGrid: '0.309',
                // New Values
                kwh_to_diesel: '0.4347826087',
                diesel_to_co2: '2.68',
                grid_kwh_to_co2: '0.309',
                hvo_kwh_to_co2: '0.195'
            },
            // Estimate
            estimateTotal_kWh: 0,
            estimateTotal_Diesel: 0,
            estimateTotal_CO2: 0,
            estimateTotal_Cost: 0,
            estimateRental_Cost: 0,
            // Usage
            usageTotal_kWh: 0,
            accommodationUsageTotal_kWh: 0,
            usageTotal_Diesel: 0,
            usageTotal_CO2: 0,
            usageTotal_Cost: 0,
            // Generation
            generationTotal_kWh: 0,
            // Savings
            usageSaving_kWh: 0,
            usageSaving_Diesel: 0,
            usageSaving_CO2: 0,
            usageSaving_Total: 0,
            usageSaving_NET: 0,
            circuits: [],
            loadCircuit: '',
            usageCircuit: '',
            usageRangeCircuit: '',
            hourlyUsageCircuit: '',
            usageByCircuit: [],
            usageRange: [],
            usageByHour: [],
            usageRangeBox: 'A',
            usageRangeBoxOccupancy: 'A',
            dataByHour: {
                usage: [],
                temperature: []
            },
            cabinDataByHour: {
                humidity: [],
                temperature: [],
                occupancy: []
            },
            usageByHourYAxis: [],
            usageByHourXAxis: [],
            tempByHour: [],
            tempByHourYAxis: [],
            tempByHourXAxis: []
        }
    },
    computed: {
        // Top Groups / Cabins by Usage - Table
        computedBox() {
            let cabinNames = this.site.cabinNames
            //if no names, set default
            if (cabinNames === '{}') {
                // get site.cabins
                let cabins = this.site.cabins
                if (cabins) {
                    cabins = JSON.parse(cabins)
                    let box = {}
                    // create box using Excel-style naming: A, B, ..., Z, AA, AB, ...
                    for (let i = 1; i < parseInt(cabins); i++) {
                        box[this.getExcelColumnName(i)] = 'Cabin ' + i
                    }
                    return box
                }
            }

            let box = {}
            if (cabinNames) {
                box = JSON.parse(cabinNames)
            }
            return box
        },
        // Check if site has any renewable/generator circuits
        hasRenewableCircuits() {
            return this.circuits.some(circuit => 
                this.isGeneratorType(circuit.type)
            )
        },
        // COMPUTED COMPARISON ENERGY MAP
        computedComparisonEnergyMap() {
            if (!this.site) return {}
            const raw = this.site.comparisonEnergyMap
            if (!raw) return {}
            
            try {
                const parsed = JSON.parse(raw)
                
                // Check if it has a TOTAL key (3D format)
                const hasTotalKey = Object.prototype.hasOwnProperty.call(parsed, 'TOTAL') && typeof parsed['TOTAL'] === 'object'
                
                // Log for debugging
                if (this.site.siteId) {
                    // console.log(`[${this.site.siteId}] 📊 comparisonEnergyMap format:`, hasTotalKey ? '3D (has TOTAL)' : '2D (legacy)')
                    // console.log(`[${this.site.siteId}] Sample keys:`, Object.keys(parsed).slice(0, 5))
                    if (hasTotalKey) {
                        // console.log(`[${this.site.siteId}] TOTAL keys:`, Object.keys(parsed['TOTAL']).slice(0, 5))
                        // console.log(`[${this.site.siteId}] TOTAL sample values:`, Object.entries(parsed['TOTAL']).slice(0, 3))
                    }
                }
                
                // If it has TOTAL key, use that (3D format)
                // Otherwise use the whole object (2D format)
                return hasTotalKey ? parsed['TOTAL'] : parsed
            } catch (e) {
                // console.error(`[${this.site?.siteId}] Error parsing comparisonEnergyMap:`, e)
                return {}
            }
        },
        // COMPUTED USAGE BY CIRCUIT
        computedUsageByCircuit() {
            // sort by value
            return this.usageByCircuit
                .filter((y) => y.circuitId !== 'total')
                .map((x) => {
                    return {
                        id: x.circuitId,
                        name: this.lookupCircuit(x.circuitId)?.name,
                        group: this.lookupCircuit(x.circuitId)?.group,
                        type: this.lookupCircuit(x.circuitId)?.type,
                        boxLetter: this.lookupCircuit(x.circuitId)?.boxLetter,
                        value: parseFloat(x.difference)
                    }
                })
                .sort((a, b) => {
                    return b.difference - a.difference
                })
                .splice(0, 10)
        },
        // COMPUTED TOTAL USAGE BY CIRCUIT
        computedTotalUsageByCircuit() {
            // sort by value
            return this.usageByCircuit
                .filter((y) => y.circuitId !== 'total')
                .map((x) => {
                    return {
                        name: this.lookupCircuit(x.circuitId)?.name,
                        group: this.lookupCircuit(x.circuitId)?.group,
                        type: this.lookupCircuit(x.circuitId)?.type,
                        boxLetter: this.lookupCircuit(x.circuitId)?.boxLetter,
                        value: parseFloat(x.lastValue)
                    }
                })
                .sort((a, b) => {
                    return b.lastValue - a.lastValue
                })
        },
        // COMPUTED USAGE BY TYPE
        computedUsageByType() {
            const sums = this.computedTotalUsageByCircuit.reduce((acc, item) => {
                acc[item.type] = (acc[item.type] || 0) + item.value
                return acc
            }, {})

            //remove entry with name UNDEFINED
            delete sums['undefined']

            return Object.entries(sums).map(([type, value]) => {
                return { name: type.toUpperCase(), text: type.toUpperCase(), value: value }
            })
        },
        // COMPUTED USAGE BY GROUP
        computedUsageByGroup() {
            let usageByGroup = this.computedTotalUsageByCircuit
            usageByGroup = usageByGroup.reduce((acc, item) => {
                acc[item.group] = (acc[item.group] || 0) + item.value
                return acc
            }, {})
            return Object.entries(usageByGroup)
                .map(([group, value]) => {
                    return { name: group, text: group, value: value }
                })
                .sort((a, b) => parseFloat(b.value) - parseFloat(a.value)) // sort descending
        },
        // COMPUTED USAGE BY BOX LETTER
        computedUsageByBoxLetter() {
            let usageByBoxLetter = this.computedTotalUsageByCircuit.reduce((acc, item) => {
                acc[item.boxLetter] = (acc[item.boxLetter] || 0) + item.value
                return acc
            }, {})
            return Object.entries(usageByBoxLetter).map(([boxLetter, value]) => {
                return { name: boxLetter, text: boxLetter, value: value }
            })
        },
        // COMPUTED AVERAGE LOAD
        computedAverageLoad() {
            return parseFloat((this.loadSeries.reduce((a, b) => a + b[1], 0) / this.loadSeries.length / 1000).toFixed(2))
        },
        // COMPUTED DAYS
        computedDays() {
            return parseInt(this.$dayjs(this.endDate).diff(this.$dayjs(this.startDate), 'day'))
        }
    },
    methods: {
        // Helper function to check if circuit type is a generator
        isGeneratorType(type) {
            const generatorTypes = ['solar', 'turbine', 'wind', 'generator', 'hydro'];
            return generatorTypes.includes(type?.toLowerCase());
        },
        // Helper function to generate Excel-style column names (A, B, ..., Z, AA, AB, ...)
        getExcelColumnName(index) {
            let name = '';
            while (index > 0) {
                index--; // Adjust for 0-indexing
                name = String.fromCharCode(65 + (index % 26)) + name;
                index = Math.floor(index / 26);
            }
            return name;
        },
        // Helper function to filter tempByHour by selected period: day, night, or both
        filterTempByHour() {
            const filteredTempByHour = {};
            //console.log('Full tempByHour data before filtering:', JSON.stringify(this.tempByHour, null, 2));

            const DAY_START = 6;   // 6 AM UTC
            const DAY_END = 18;    // 6 PM UTC

            Object.keys(this.tempByHour).forEach(key => {
                const timestamp = this.tempByHour[key][0];
                const date = new Date(timestamp);
                const hourOfDay = date.getUTCHours(); // Ensures timestamps are treated as UTC
                //const entryValue = this.tempByHour[key][1];  // Temperature or whatever value is being tracked

                //console.log(`Key: ${key} [Type: ${typeof key}], Timestamp: ${timestamp} [${date}], HourOfDay: ${hourOfDay}, Value: ${entryValue}`);

                if (this.day && this.night) {
                    filteredTempByHour[key] = this.tempByHour[key];
                    //console.log(`✅ Including Hour ${hourOfDay} (Combined Mode)`);
                }
                else if (this.day && !this.night) {
                    if (hourOfDay >= DAY_START && hourOfDay < DAY_END) {
                        filteredTempByHour[key] = this.tempByHour[key];
                        //console.log(`✅ Including Hour ${hourOfDay} (Daytime)`);
                    }
                }
                else if (!this.day && this.night) {
                    if (hourOfDay >= DAY_END || hourOfDay < DAY_START) {
                        filteredTempByHour[key] = this.tempByHour[key];
                        //console.log(`✅ Including Hour ${hourOfDay} (Nighttime)`);
                    }
                }
            });

            //console.log('FilteredTempByHour after filtering:', JSON.stringify(filteredTempByHour, null, 2));
            return filteredTempByHour;
        },
        // GET SITE DETAILS
        getSite: async function () {
            const siteID = this.$route.params.id
            await this.REDIS_read('site', siteID).then((result) => {
                this.site = result.data
                //console.log('useMainMeter:', this.site?.useMainMeter);
            })
        },
        // GET LOAD OVER TIME
        async getLoadOverTime() {
            let url = `${process.env.VUE_APP_API_URL}publicV2/timeseries/rangeload`
            let timeseries = ''
            if (this.loadCircuit === '') {
                //check if useMainMeter is "1"
                if (this.site?.useMainMeter === '1') {
                    timeseries = `ts:${this.site.siteId}:mainMeter:load:total`
                } else {
                    timeseries = `ts:${this.site.siteId}:load:total`
                }
            } else {
                timeseries = `ts:${this.site.siteId}:load:${this.loadCircuit}`
            }
            // Don't get data before site start date
            if (this.startDateUnix < this.$dayjs(this.site.startDateTime)) {
                this.startDateUnix = this.$dayjs(this.site.startDateTime).tz('Europe/London').startOf('day').valueOf()
            }
            await this.$axios
                .post(url, {
                    orgId: this.site.orgId,
                    timeseries: timeseries,
                    reverseRange: false,
                    aggregation: null,
                    startTimestamp: this.startDateUnix,
                    endTimestamp: this.endDateUnix,
                    timestampFormat: 'valueOf',
                    outputFormat: 'json'
                })
                .then((res) => {
                    // Day / Night
                    if (this.day && this.night) {
                        this.loadSeries = res.data.data.map((item) => {
                            return [item.timestamp, item.value]
                        })
                    } else if (this.day && !this.night) {
                        this.loadSeries = res.data.data.map((item) => {
                            let hour = this.$dayjs(item.timestamp).tz('Europe/London').hour()
                            return hour >= 6 && hour <= 18 ? [item.timestamp, item.value] : [item.timestamp, 0]
                        })
                    } else if (!this.day && this.night) {
                        this.loadSeries = res.data.data.map((item) => {
                            let hour = this.$dayjs(item.timestamp).tz('Europe/London').hour()
                            return hour < 6 || hour > 18 ? [item.timestamp, item.value] : [item.timestamp, 0]
                        })
                    } else {
                        this.loadSeries = []
                    }
                })
                .catch(() => {
                    // console.error(err)
                })
        },
        // GET USAGE OVER TIME
        async getUsageOverTime() {
            let url = `${process.env.VUE_APP_API_URL}publicV2/timeseries/range`
            let timeseries = ''
            if (this.usageCircuit === '') {
                //check if useMainMeter is "1"
                if (this.site?.useMainMeter === '1') {
                    timeseries = `ts:${this.site.siteId}:mainMeter:usage:total`
                } else {
                    timeseries = `ts:${this.site.siteId}:usage:total`
                }
            } else {
                timeseries = `ts:${this.site.siteId}:usage:${this.usageCircuit}`
            }
            // Don't get data before site start date
            if (this.startDateUnix < this.$dayjs(this.site.startDateTime)) {
                this.startDateUnix = this.$dayjs(this.site.startDateTime).tz('Europe/London').startOf('day').valueOf()
            }
            await this.$axios
                .post(url, {
                    orgId: this.site.orgId,
                    timeseries: timeseries,
                    reverseRange: false,
                    aggregation: null,
                    startTimestamp: this.startDateUnix,
                    endTimestamp: this.endDateUnix,
                    timestampFormat: 'valueOf',
                    outputFormat: 'array'
                })
                .then((res) => {
                    // Day / Night
                    if (this.day && this.night) {
                        this.usageTimeseries = res.data.data
                    } else if (this.day && !this.night) {
                        this.usageTimeseries = res.data.data.filter((item) => {
                            return this.$dayjs(item[0]).tz('Europe/London').hour() >= 6 && this.$dayjs(item[0]).tz('Europe/London').hour() <= 18
                        })
                    } else if (!this.day && this.night) {
                        this.usageTimeseries = res.data.data.filter((item) => {
                            return this.$dayjs(item[0]).tz('Europe/London').hour() < 6 || this.$dayjs(item[0]).tz('Europe/London').hour() > 18
                        })
                    } else {
                        this.usageTimeseries = []
                    }

                    //create usage by day for the Total usage
                    let usageByDay = []
                    let usageByNight = []
                    const totalDays = this.$dayjs(this.endDate).diff(this.$dayjs(this.startDate), 'day')
                    let xAxis = []

                    for (let i = 0; i <= totalDays; i++) {
                        let day = this.$dayjs(this.startDate).add(i, 'day').format('DD-MMM-YY')
                        xAxis.push(day)
                        let overallDayUsage = res.data.data.filter((item) => {
                            return (
                                this.$dayjs(item[0]) >= this.$dayjs(this.startDate).add(i, 'day') &&
                                this.$dayjs(item[0]) <= this.$dayjs(this.startDate).add(i + 1, 'day')
                            )
                        })

                        let dayUsage = []
                        let nightUsage = []

                        for (let j of overallDayUsage) {
                            if (this.$dayjs(j[0]).hour() <= 18) {
                                dayUsage.push(j)
                            } else {
                                nightUsage.push(j)
                            }
                        }

                        try {
                            const firstDayValue = dayUsage[0][1];
                            const lastDayValue = dayUsage[dayUsage.length - 1][1];
                            const firstNightValue = nightUsage[0]?.[1] || 0;
                            const lastNightValue = nightUsage[nightUsage.length - 1]?.[1] || 0;
                            
                            // Calculate differences, but handle counter resets
                            // If lastValue < firstValue, it means counter reset - use lastValue only
                            let dayTotal = lastDayValue >= firstDayValue 
                                ? Math.floor(lastDayValue - firstDayValue)
                                : Math.floor(lastDayValue); // Counter reset - use accumulated value since reset
                            
                            let nightTotal = lastNightValue >= firstNightValue
                                ? Math.floor(lastNightValue - firstNightValue)
                                : Math.floor(lastNightValue); // Counter reset
                            
                            // Ensure non-negative values
                            dayTotal = Math.max(0, dayTotal);
                            nightTotal = Math.max(0, nightTotal);
                            
                            usageByDay.push(dayTotal);
                            usageByNight.push(nightTotal);
                        } catch (e) {
                            // No data for this day - push 0 to maintain alignment with xAxis
                            usageByDay.push(0)
                            usageByNight.push(0)
                        }
                    }
                    this.usageTimeseriesDay = usageByDay
                    this.usageTimeseriesNight = usageByNight
                    this.usageTimeSeriesXAxis = xAxis
                })
                .catch(() => {
                    // console.error(err)
                })
        },
        // GET USAGE TOTAL
        async getUsageTotal() {
            try {
                let usageByCircuitFiltered = this.usageByCircuit
                    .filter((y) => y.circuitId !== 'total')
                    .map((x) => {
                        const circuit = this.lookupCircuit(x.circuitId) || {};
                        return {
                            id: x.circuitId,
                            name: circuit.name,
                            group: circuit.group,
                            type: circuit.type,
                            boxLetter: circuit.boxLetter,
                            value: parseFloat(x.difference)
                        };
                    });

                let selectedCircuitsLocal = this.selectedSiteCircuits;

                if (selectedCircuitsLocal.length > 0) {
                    const hasOnlyDayOrNight = (this.day && !this.night) || (!this.day && this.night);

                    // === MAIN METER PATH ===
                    if (this.site?.useMainMeter === '1') {
                        const timeseries = `ts:${this.site.siteId}:mainMeter:usage:total`;
                        const url = `${process.env.VUE_APP_API_URL}publicV2/timeseries/usagetotal`;

                        if (this.startDateUnix <= this.$dayjs(this.site.startDateTime)) {
                            this.startDateUnix = this.$dayjs(this.site.startDateTime)
                                .tz('Europe/London')
                                .startOf('day')
                                .valueOf();
                        }

                        await this.$axios.post(url, {
                            orgId: this.site.orgId,
                            timeseries,
                            startTimestamp: this.startDateUnix,
                            endTimestamp: this.endDateUnix,
                            day: this.day,
                            night: this.night,
                            reverseRange: false,
                            aggregation: 'avg',
                            bucketSize: 3600000,
                            timestampFormat: 'valueOf',
                            outputFormat: 'json'
                        }).then((res) => {
                            this.usageTotal_kWh = parseFloat(res.data.data.toFixed(2));
                        }).catch(() => {
                            // console.error(err);
                        });

                        return;
                    }

                    // === CIRCUIT-BASED SITE, WITH DAY OR NIGHT FILTERING ===
                    if (hasOnlyDayOrNight) {
                        const timeseries = `ts:${this.site.siteId}:usage:total`;
                        const url = `${process.env.VUE_APP_API_URL}publicV2/timeseries/usagetotal`;

                        if (this.startDateUnix <= this.$dayjs(this.site.startDateTime)) {
                            this.startDateUnix = this.$dayjs(this.site.startDateTime)
                                .tz('Europe/London')
                                .startOf('day')
                                .valueOf();
                        }

                        await this.$axios.post(url, {
                            orgId: this.site.orgId,
                            timeseries,
                            startTimestamp: this.startDateUnix,
                            endTimestamp: this.endDateUnix,
                            day: this.day,
                            night: this.night,
                            reverseRange: false,
                            aggregation: 'avg',
                            bucketSize: 3600000,
                            timestampFormat: 'valueOf',
                            outputFormat: 'json'
                        }).then((res) => {
                            this.usageTotal_kWh = parseFloat(res.data.data.toFixed(2));
                        }).catch(() => {
                            // console.error(err);
                        });

                        return;
                    }

                    // === CIRCUIT-BASED AGGREGATE TOTAL ===
                    selectedCircuitsLocal = this.circuits.map((c) => c.id);
                    usageByCircuitFiltered = usageByCircuitFiltered.filter((c) =>
                        selectedCircuitsLocal.includes(c.id)
                    );

                    // Calculate total, subtracting generator values
                    const totalUsage = usageByCircuitFiltered.reduce((acc, item) => {
                        const circuit = this.lookupCircuit(item.id);
                        const isGenerator = this.isGeneratorType(circuit?.type);

                        if (isGenerator) {
                            // Cumulative counter semantics (see getGenerationTotal):
                            //   - normal case: non-negative increment = last - first
                            //   - NEGATIVE diff: meter reset mid-window; `last` is the
                            //     accumulation since the reset (understates by the
                            //     pre-reset part, but is the best we have)
                            //   - ZERO diff: no generation — a real result, NOT a fault.
                            //     Do NOT fall back to lastValue (that would subtract the
                            //     entire standing counter of a static circuit).
                            const raw = this.usageByCircuit.find(c => c.circuitId === item.id);
                            const first = parseFloat(raw?.firstValue);
                            const last = parseFloat(raw?.lastValue);
                            const diff = last - first;
                            const generatorValue = diff < 0 ? Math.max(last, 0) : diff;
                            return acc - (Number.isFinite(generatorValue) ? generatorValue : 0);
                        }

                        return acc + item.value;
                    }, 0);
                    
                    // console.log(`📊 Usage Total Calculation: ${totalUsage.toFixed(2)} kWh`);
                    this.usageTotal_kWh = parseFloat(totalUsage.toFixed(2));
                }
            } catch (e) {
                // console.error('Error: ' + e);
            }
        },

        // GET GENERATION TOTAL
        async getGenerationTotal() {
            try {
                let totalGeneration = 0;
                
                // console.log('🔋 getGenerationTotal - Total circuits in usageByCircuit:', this.usageByCircuit.length);
                
                // Calculate generation from circuits with generator types
                const generatorCircuits = this.usageByCircuit
                    .filter((y) => y.circuitId !== 'total')
                    .filter((x) => {
                        const circuit = this.lookupCircuit(x.circuitId);
                        const isGenerator = this.isGeneratorType(circuit?.type);
                        // if (isGenerator) {
                        //     console.log(`  Circuit ${x.circuitId}: type="${circuit?.type}", difference=${x.difference}, lastValue=${x.lastValue}, firstValue=${x.firstValue}`);
                        // }
                        return isGenerator;
                    });
                
                // console.log('🔋 Found generator circuits:', generatorCircuits.length);
                
              totalGeneration = generatorCircuits.reduce((acc, item) => {
                    const first = parseFloat(item.firstValue);
                    const last  = parseFloat(item.lastValue);
                    const diff  = last - first;

                    // Cumulative counter: normal case is a non-negative increment.
                    // A NEGATIVE diff means the meter reset mid-window, so `last` is
                    // the accumulation since the reset (understates by the pre-reset part).
                    // A ZERO diff means no generation — that is a real result, not a fault.
                    const value = diff < 0 ? Math.max(last, 0) : diff;

                    return acc + (Number.isFinite(value) ? value : 0);
                }, 0);
                
                // console.log('🔋 Total generation:', totalGeneration);
                this.generationTotal_kWh = parseFloat(totalGeneration.toFixed(2));
            } catch (error) {
                console.error('Error in getGenerationTotal:', error);
                this.generationTotal_kWh = 0;
            }
        },

        // GET ACCOMMODATION USAGE TOTAL
        async getAccommodationUsageTotal() {
            try {
                const hasOnlyDayOrNight = (this.day && !this.night) || (!this.day && this.night);
                const bothOrNoneSelected = (this.day && this.night) || (!this.day && !this.night);
                const orgId = this.site.orgId;
                const siteId = this.site.siteId;

                let totalUsage = 0;

                if (hasOnlyDayOrNight) {
                    // === DAY OR NIGHT FILTERING ===
                    for (const circuitId of this.accommodationCircuits) {
                        // Check if circuit is a generator - exclude from accommodation usage
                        const circuit = this.lookupCircuit(circuitId);
                        const isGenerator = this.isGeneratorType(circuit?.type);
                        if (isGenerator) continue;

                        const timeseries = `ts:${siteId}:usage:${circuitId}`;
                        const url = `${process.env.VUE_APP_API_URL}publicV2/timeseries/usagetotal`;

                        // Adjust for site start date
                        if (this.startDateUnix <= this.$dayjs(this.site.startDateTime)) {
                            this.startDateUnix = this.$dayjs(this.site.startDateTime)
                                .tz('Europe/London')
                                .startOf('day')
                                .valueOf();
                        }

                        const response = await this.$axios.post(url, {
                            orgId,
                            timeseries,
                            startTimestamp: this.startDateUnix,
                            endTimestamp: this.endDateUnix,
                            day: this.day,
                            night: this.night,
                            reverseRange: false,
                            aggregation: 'avg',
                            bucketSize: 3600000,
                            timestampFormat: 'valueOf',
                            outputFormat: 'json'
                        });

                        totalUsage += response.data.data;
                    }
                } else if (bothOrNoneSelected) {
                    // === BOTH DAY AND NIGHT OR NEITHER SELECTED ===
                    const url = `${process.env.VUE_APP_API_URL}publicV2/timeseries/mrange/difference`;

                    const response = await this.$axios.post(url, {
                        orgId,
                        startTimestamp: this.startDateUnix,
                        endTimestamp: this.endDateUnix,
                        labels: [`orgId=${orgId}`, `siteId=${siteId}`, `type=usage`]
                    });

                    const allCircuits = response.data.data;
                    const filtered = allCircuits.filter(item =>
                        this.accommodationCircuits.includes(item.circuitId)
                    );

                    // Calculate total, excluding generators (don't subtract, just exclude)
                    totalUsage = filtered.reduce((acc, item) => {
                        const circuit = this.lookupCircuit(item.circuitId);
                        const isGenerator = this.isGeneratorType(circuit?.type);
                        // Exclude generators from accommodation usage
                        if (isGenerator) return acc;
                        return acc + item.difference;
                    }, 0);
                }

                this.accommodationUsageTotal_kWh = parseFloat(totalUsage.toFixed(2));
            } catch (error) {
                // console.error('Error in getAccommodationUsageTotal:', error);
            }
        },
        // GET USAGE FIRST VALUE BY RANGE
        async getUsageFirstValueByRange() {
            let url = `${process.env.VUE_APP_API_URL}publicV2/timeseries/firstvaluebyrange`
            let timeseries = `ts:${this.site.siteId}:usage:total`
            if (this.site?.userMainMeter === '1') {
                timeseries = `ts:${this.site.siteId}:mainMeter:usage:total`
            }
            // Don't get data before site start date
            if (this.startDateUnix < this.$dayjs(this.site.startDateTime)) {
                this.startDateUnix = this.$dayjs(this.site.startDateTime).tz('Europe/London').startOf('day').valueOf()
            }
            await this.$axios
                .post(url, {
                    orgId: this.site.orgId,
                    timeseries: timeseries,
                    startTimestamp: this.startDateUnix,
                    endTimestamp: this.endDateUnix,
                    day: this.day,
                    night: this.night
                })
                .then((res) => {
                    this.usage_kWh.firstValue = res.data.data.value
                })
                .catch(() => {
                    // console.error(err)
                })
        },
        // GET USAGE LAST VALUE BY RANGE
        async getUsageLastValueByRange() {
            let url = `${process.env.VUE_APP_API_URL}publicV2/timeseries/lastvaluebyrange`
            let timeseries = `ts:${this.site.siteId}:usage:total`
            if (this.site?.useMainMeter === '1') {
                timeseries = `ts:${this.site.siteId}:mainMeter:usage:total`
            }
            // Don't get data before site start date
            if (this.startDateUnix < this.$dayjs(this.site.startDateTime)) {
                this.startDateUnix = this.$dayjs(this.site.startDateTime).tz('Europe/London').startOf('day').valueOf()
            }
            await this.$axios
                .post(url, {
                    orgId: this.site.orgId,
                    timeseries: timeseries,
                    startTimestamp: this.startDateUnix,
                    endTimestamp: this.endDateUnix,
                    day: this.day,
                    night: this.night
                })
                .then((res) => {
                    this.usage_kWh.lastValue = res.data.data.value
                })
                .catch(() => {
                    // console.error(err)
                })
        },
        // GET THE USAGE RANGE
        async getUsageRange() {
            let url = `${process.env.VUE_APP_API_URL}publicV2/timeseries/range`
            let t = this
            var timeseries = ''
            if (this.usageRangeCircuit === '') {
                //check if useMainMeter is "1"
                if (this.site?.useMainMeter === '1') {
                    timeseries = `ts:${this.site.siteId}:mainMeter:usage:total`
                } else {
                    timeseries = `ts:${this.site.siteId}:usage:total`
                }
            } else {
                timeseries = `ts:${this.site.siteId}:usage:${this.usageRangeCircuit}`
            }
            // Don't get data before site start date
            if (this.startDateUnix < this.$dayjs(this.site.startDateTime)) {
                this.startDateUnix = this.$dayjs(this.site.startDateTime).tz('Europe/London').startOf('day').valueOf()
            }
            await this.$axios
                .post(url, {
                    orgId: t.site.orgId,
                    timeseries: timeseries,
                    reverseRange: false,
                    aggregation: 'avg',
                    bucketSize: 3600000,
                    startTimestamp: t.startDateUnix,
                    endTimestamp: t.endDateUnix,
                    timestampFormat: 'valueOf',
                    outputFormat: 'json'
                })
                .then((res) => {
                    // Day / Night
                    let data = []
                    if (this.day && this.night) {
                        data = res.data.data
                    } else if (this.day && !this.night) {
                        data = res.data.data.filter((item) => {
                            let hour = this.$dayjs(item.timestamp).tz('Europe/London').hour()
                            return hour >= 5 && hour < 18
                        })
                    } else if (!this.day && this.night) {
                        data = res.data.data.filter((item) => {
                            let hour = this.$dayjs(item.timestamp).tz('Europe/London').hour()
                            // Keep all night-time data
                            return hour < 6 || hour >= 17
                        })
                    } else {
                        data = []
                    }

                    const thirtyMinutesInMilliseconds = 1800000
                    let result = []
                    for (let i = 1; i < data.length; i++) {
                        let item = data[i]
                        const currentVal = parseFloat(data[i - 1].value)
                        const nextVal = parseFloat(item.value)
                        let delta = nextVal - currentVal
                        
                        // METER RESET DETECTION: If reading drops by >70%, this is a meter reset
                        if (delta < 0 && Math.abs(delta) > (currentVal * 0.70)) {
                            // console.log(`[METER RESET] Detected at ${this.$dayjs(item.timestamp).format('YYYY-MM-DD HH:mm')}`)
                            // console.log(`  Before: ${currentVal.toFixed(2)} kWh | After: ${nextVal.toFixed(2)} kWh`)
                            // Use current reading as the delta (cumulative usage before reset)
                            delta = currentVal
                            // console.log(`  Preserved usage: ${delta.toFixed(2)} kWh`)
                        }
                        
                        // Clamp negative values to 0 (protects against noise)
                        delta = Math.max(0, delta)
                        
                        let value = [item.timestamp + thirtyMinutesInMilliseconds, parseFloat(delta.toFixed(2))]
                        result.push(value)
                    }

                    // Function to calculate quartile
                    function quartile(array, percentile) {
                        array.sort((a, b) => a - b)
                        const index = percentile * (array.length - 1)
                        const lower = Math.floor(index)
                        const remainder = index - lower
                        if (array[lower + 1] !== undefined) {
                            return array[lower] + remainder * (array[lower + 1] - array[lower])
                        } else {
                            return array[lower]
                        }
                    }

                    // Extract the values and sort
                    let values = result.map((item) => parseFloat(item[1])).sort((a, b) => a - b)

                    // Compute Q1, Q3, and IQR, default percentile is 0.25 for Q1 and 0.75 for Q3
                    let Q1 = quartile(values, 0.25)
                    let Q3 = quartile(values, 0.8)
                    let IQR = Q3 - Q1

                    // Define the bounds for outliers
                    //change this to allow for more breathing room for outliers, default is 1.5
                    const breathingRoom = 3

                    let lowerBound = Q1 - breathingRoom * IQR
                    let upperBound = Q3 + breathingRoom * IQR

                    //console.log('result = ' + JSON.stringify(result, null, 2))
                    //console.log('startTimestamp = ' + t.startDateUnix)
                    //console.log('endTimestamp = ' + t.endDateUnix)

                    // Filters our data for non-outliers
                    t.usageRange = result.filter((item) => {
                        let v = parseFloat(item[1])
                        return v >= lowerBound && v <= upperBound
                    })

                    // Remove the 5 AM data point for display if in day mode
                    // if (this.day && !this.night) {
                    //     t.usageRange = t.usageRange.filter(
                    //         (item) =>
                    //             this.$dayjs(item[0] - thirtyMinutesInMilliseconds)
                    //                 .tz('Europe/London')
                    //                 .hour() !== 5
                    //     )
                    // }
                    //
                    // // Remove the 5 PM data point for display if in day mode
                    // if (!this.day && this.night) {
                    //     t.usageRange = t.usageRange.filter(
                    //         (item) =>
                    //             this.$dayjs(item[0] - thirtyMinutesInMilliseconds)
                    //                 .tz('Europe/London')
                    //                 .hour() !== 17
                    //     )
                    // }
                })
                .catch(() => {
                    // console.error(err)
                })
        },
        // DOWNLOAD HOURLY USAGE
        async downloadHourlyUsage() {
            const data = this.usageRange.map((row) => ({
                timestamp: this.$dayjs(row[0], 'x').format('DD-MMM-YYYY HH:mm'),
                watts: row[1]
            }))
            let csv = this.$papa.unparse(data)
            this.$papa.download(csv, 'Hourly_Usage')
        },
        // GET USAGE BY CIRCUIT
        async getUsageByCircuit() {
            let url = `${process.env.VUE_APP_API_URL}publicV2/timeseries/mrange/difference`
            let t = this
            // Don't get data before site start date
            if (this.startDateUnix < this.$dayjs(this.site.startDateTime)) {
                this.startDateUnix = this.$dayjs(this.site.startDateTime).tz('Europe/London').startOf('day').valueOf()
            }
            await this.$axios
                .post(url, {
                    orgId: this.site.orgId,
                    startTimestamp: this.startDateUnix,
                    endTimestamp: this.endDateUnix,
                    labels: ['type=usage', `siteId=${t.site.siteId}`],
                    day: this.day,
                    night: this.night
                })
                .then((res) => {
                    // Day / Night
                    this.usageByCircuit = res.data.data
                    // console.log('usageByCircuit', JSON.stringify(this.usageByCircuit, null, 2))
                })
                .catch(() => {
                    // console.error(err)
                })
        },
        // CALCULATE DASHBOARD DATA
        async calculate() {
            this.loading = true
            if (this.startDate === null) {
                this.startDate = this.initStartDate
            }
            if (this.endDate === null) {
                this.endDate = this.initEndDate
            }
            this.startDateUnix = this.$dayjs(this.startDate).tz('Europe/London').startOf('day').valueOf()
            this.endDateUnix = this.$dayjs(this.endDate).tz('Europe/London').endOf('day').valueOf()
            // Don't get data before site start date
            if (this.startDateUnix < this.$dayjs(this.site.startDateTime)) {
                this.startDateUnix = this.$dayjs(this.site.startDateTime).tz('Europe/London').startOf('day').valueOf()
            }
            // Refresh circuits to get latest types
            await this.getCircuits()
            // Get Data
            await this.getLoadOverTime()
            await this.getUsageOverTime()
            await this.getUsageFirstValueByRange()
            await this.getUsageLastValueByRange()
            await this.getUsageByCircuit()
            await this.getUsageRange()
            await this.getUsageByHour()
            await this.getTempByHour()
            await this.getUsageTotal()
            await this.getAccommodationUsageTotal()
            await this.getGenerationTotal()
            this.dataByHour = {
                usage: this.usageByHour,
                temperature: this.tempByHour
            }
            this.cabinDataByHour = {
                humidity: this.cabinHumidityByHour,
                temperature: this.cabinTempByHour,
                occupancy: this.cabinOccupancyByHour
            }

            // Filter the temperature data once, based on this.day and this.night
            const filteredTempByHour = this.filterTempByHour();
            const filteredTempByHourArray = Object.values(filteredTempByHour);

            // console.log(`[${this.site.siteId}] 🌡️ Filtered temperature data points:`, filteredTempByHourArray.length)
            // console.log(`[${this.site.siteId}] 🗺️ computedComparisonEnergyMap keys:`, Object.keys(this.computedComparisonEnergyMap || {}).slice(0, 10))
            // console.log(`[${this.site.siteId}] 🗺️ computedComparisonEnergyMap sample:`, Object.entries(this.computedComparisonEnergyMap || {}).slice(0, 3))

            switch (this.site.type) {
                case 'Diesel':
                    // Estimate Calculation
                    this.estimateTotal_kWh = parseFloat(await this.calculateEstimatedEnergyUsage(filteredTempByHourArray, this.computedComparisonEnergyMap))
                    this.estimateTotal_Diesel = parseFloat((parseFloat(this.estimateTotal_kWh) * parseFloat(this.conversion.kWhToDiesel)).toFixed(2))
                    this.estimateTotal_CO2 = parseFloat((parseFloat(this.estimateTotal_Diesel) * parseFloat(this.conversion.diesel_to_co2)).toFixed(2))
                    this.estimateTotal_Cost = parseFloat((parseFloat(this.estimateTotal_Diesel) * parseFloat(this.site.costPerLitre)).toFixed(2))
                    // Usage Calculation
                    this.usageTotal_Diesel = parseFloat((parseFloat(this.accommodationUsageTotal_kWh) * parseFloat(this.conversion.kWhToDiesel)).toFixed(2))
                    this.usageTotal_CO2 = parseFloat((parseFloat(this.usageTotal_Diesel) * parseFloat(this.conversion.diesel_to_co2)).toFixed(2))
                    this.usageTotal_Cost = parseFloat((parseFloat(this.usageTotal_Diesel) * parseFloat(this.site.costPerLitre)).toFixed(2))
                    break
                case 'Green Electricity':
                    // Estimate Calculation
                    this.estimateTotal_kWh = parseFloat(await this.calculateEstimatedEnergyUsage(filteredTempByHourArray, this.computedComparisonEnergyMap))
                    this.estimateTotal_CO2 = parseFloat((parseFloat(this.estimateTotal_kWh) * parseFloat(this.conversion.grid_kwh_to_co2)).toFixed(2))
                    this.estimateTotal_Cost = parseFloat(
                        (parseFloat(this.estimateTotal_kWh) * parseFloat(this.site.costPerkWh) + this.site.costStandingCharge * this.computedDays).toFixed(2)
                    )
                    // Usage Calculation
                    this.usageTotal_CO2 = parseFloat((parseFloat(this.accommodationUsageTotal_kWh) * parseFloat(this.conversion.grid_kwh_to_co2)).toFixed(2))
                    this.usageTotal_Cost = parseFloat(
                        (parseFloat(this.accommodationUsageTotal_kWh) * parseFloat(this.site.costPerkWh) + this.site.costStandingCharge * this.computedDays).toFixed(
                            2
                        )
                    )
                    break
                case 'Grid':
                    // Estimate Calculation
                    this.estimateTotal_kWh = parseFloat(await this.calculateEstimatedEnergyUsage(filteredTempByHourArray, this.computedComparisonEnergyMap))
                    this.estimateTotal_CO2 = parseFloat((parseFloat(this.estimateTotal_kWh) * parseFloat(this.conversion.grid_kwh_to_co2)).toFixed(2))
                    this.estimateTotal_Cost = parseFloat(
                        (parseFloat(this.estimateTotal_kWh) * parseFloat(this.site.costPerkWh) + this.site.costStandingCharge * this.computedDays).toFixed(2)
                    )
                    // Usage Calculation
                    this.usageTotal_CO2 = parseFloat((parseFloat(this.accommodationUsageTotal_kWh) * parseFloat(this.conversion.grid_kwh_to_co2)).toFixed(2))
                    this.usageTotal_Cost = parseFloat(
                        (parseFloat(this.accommodationUsageTotal_kWh) * parseFloat(this.site.costPerkWh) + this.site.costStandingCharge * this.computedDays).toFixed(
                            2
                        )
                    )
                    break
                case 'HVO':
                    // Estimate Calculation
                    this.estimateTotal_kWh = parseFloat(await this.calculateEstimatedEnergyUsage(filteredTempByHourArray, this.computedComparisonEnergyMap))
                    this.estimateTotal_CO2 = parseFloat((parseFloat(this.estimateTotal_kWh) * parseFloat(this.conversion.hvo_kwh_to_co2)).toFixed(2))
                    this.estimateTotal_Cost = parseFloat(
                        (parseFloat(this.estimateTotal_kWh) * parseFloat(this.site.costPerkWh) + this.site.costStandingCharge * this.computedDays).toFixed(2)
                    )
                    // Usage Calculation
                    this.usageTotal_CO2 = parseFloat((parseFloat(this.accommodationUsageTotal_kWh) * parseFloat(this.conversion.hvo_kwh_to_co2)).toFixed(2))
                    this.usageTotal_Cost = parseFloat(
                        (parseFloat(this.accommodationUsageTotal_kWh) * parseFloat(this.site.costPerkWh) + this.site.costStandingCharge * this.computedDays).toFixed(
                            2
                        )
                    )
                    break
            }

            // Estimate Rental Cost
            this.estimateRental_Cost = parseFloat(this.computedDays.toFixed(2) * parseFloat(this.site.rentalCost.toFixed(2)))

            // Calulate Savings
            switch (this.site.mode) {
                case 'monitoring':
                    this.usageSaving_kWh = 0
                    this.usageSaving_Diesel = 0
                    this.usageSaving_CO2 = 0
                    this.usageSaving_Total = 0
                    this.usageSaving_NET = 0
                    break
                default:
                    // console.log(this.estimateTotal_kWh, this.accommodationUsageTotal_kWh)
                    this.usageSaving_kWh = parseFloat((parseFloat(this.estimateTotal_kWh) - parseFloat(this.accommodationUsageTotal_kWh)).toFixed(2))
                    this.usageSaving_Diesel = parseFloat((parseFloat(this.estimateTotal_Diesel) - parseFloat(this.usageTotal_Diesel)).toFixed(2))
                    this.usageSaving_CO2 = parseFloat((parseFloat(this.estimateTotal_CO2) - parseFloat(this.usageTotal_CO2)).toFixed(2))
                    this.usageSaving_Total = parseFloat((parseFloat(this.estimateTotal_Cost) - parseFloat(this.usageTotal_Cost)).toFixed(2))
                    this.usageSaving_NET = parseFloat((parseFloat(this.usageSaving_Total) - parseFloat(this.estimateRental_Cost)).toFixed(2))
                    break
            }
            this.chartKey++
            this.loading = false
        },
        // GET CIRCUITS
        async getCircuits() {
            //console.log('getCircuits')
            let url = `${process.env.VUE_APP_API_URL}publicV2/circuits`
            await this.$axios
                .post(url, {
                    orgId: this.site.orgId,
                    siteId: this.site.siteId
                })
                .then((res) => {
                    let allCircuits = res.data.data.documents.map((x) => x.value)
                    
                    // Deduplicate circuits by id, preferring configured ones (with parentGroup or accomodation)
                    const circuitMap = new Map()
                    for (const circuit of allCircuits) {
                        const existing = circuitMap.get(circuit.id)
                        if (!existing) {
                            circuitMap.set(circuit.id, circuit)
                        } else {
                            // Prefer the configured circuit (has parentGroup or accomodation)
                            if ((circuit.parentGroup || circuit.accomodation) && !existing.parentGroup && !existing.accomodation) {
                                circuitMap.set(circuit.id, circuit)
                            }
                        }
                    }
                    this.circuits = Array.from(circuitMap.values())
                    
                    //console.log('circuits.length = ' + this.circuits.length)
                    // loop through circuits and where circuit.accommodation is true add the circuit ids to accommodationCircuits
                    for (let i = 0; i < this.circuits.length; i++) {
                        let circuit = this.circuits[i]
                        if (circuit.accomodation === true) {
                            // console.log('accomodation Circuit: ' + circuit.id)
                            this.accommodationCircuits.push(circuit.id)
                        }
                    }

                    //add 'All'
                    this.circuits.unshift({
                        id: 'All',
                        name: 'All',
                        group: 'All',
                        type: 'All',
                        boxLetter: 'All'
                    })

                    this.selectedSiteCircuits = ['All']
                })
                .catch(() => {
                    // console.error('***** ' + err)
                })
        },
        // LOOKUP CIRCUIT - prefer configured circuits (with parentGroup or accomodation)
        lookupCircuit(circuitId) {
            // Find all circuits with matching id
            let matchingCircuits = this.circuits.filter((x) => x.id === circuitId)
            // Prefer the one that has been configured (has parentGroup or accomodation flag)
            let circuit = matchingCircuits.find((x) => x.parentGroup || x.accomodation) || matchingCircuits[0]
            if (circuit !== undefined) {
                if (circuit?.group === undefined || circuit?.group === '' || circuit?.group === null) {
                    circuit.group = circuit?.parentGroup || 'Unknown'
                    return circuit
                } else {
                    return circuit
                }
            }
        },
        // GET USAGE BY HOUR
        async getUsageByHour() {
            let t = this
            let url = `${process.env.VUE_APP_API_URL}publicV2/timeseries/range`
            let timeseries = ''
            if (this.hourlyUsageCircuit === '') {
                timeseries = `ts:${this.site.siteId}:usage:total`
            } else {
                timeseries = `ts:${this.site.siteId}:usage:${this.hourlyUsageCircuit}`
            }
            const thirtyMinutesInMilliseconds = 30 * 60 * 1000
            // let timeseries = `ts:${t.site.siteId}:usage:total`
            // Don't get data before site start date
            if (this.startDateUnix < this.$dayjs(this.site.startDateTime)) {
                this.startDateUnix = this.$dayjs(this.site.startDateTime).tz('Europe/London').startOf('day').valueOf()
            }
            await t.$axios
                .post(url, {
                    orgId: t.site.orgId,
                    timeseries: timeseries,
                    reverseRange: false,
                    aggregation: 'avg',
                    bucketSize: 3600000,
                    startTimestamp: t.startDateUnix,
                    endTimestamp: t.endDateUnix,
                    timestampFormat: 'valueOf',
                    outputFormat: 'array'
                })
                .then((res) => {
                    // Log the raw response data
                    const dataArray = []
                    // const xAxis = []
                    const yAxis = []
                    let data = []
                    // Day / Night
                    if (this.day && this.night) {
                        data = res.data.data
                    } else if (this.day && !this.night) {
                        data = res.data.data.filter((item) => {
                            return this.$dayjs(item[0]).tz('Europe/London').hour() >= 6 && this.$dayjs(item[0]).tz('Europe/London').hour() <= 18
                        })
                    } else if (!this.day && this.night) {
                        data = res.data.data.filter((item) => {
                            return this.$dayjs(item[0]).tz('Europe/London').hour() < 6 || this.$dayjs(item[0]).tz('Europe/London').hour() > 18
                        })
                    } else {
                        data = []
                    }
                    for (let i = 0; i < data.length - 1; i++) {
                        const currentTimestamp = data[i][0]
                        const currentValue = data[i][1]
                        const nextValue = data[i + 1][1]
                        dataArray.push([currentTimestamp + thirtyMinutesInMilliseconds, parseFloat((nextValue - currentValue).toFixed(2))])
                        // xAxis.push(currentTimestamp)
                        yAxis.push(parseFloat((nextValue - currentValue).toFixed(2)))
                    }
                    // Log the processed data array

                    // Function to calculate quartile
                    function quartile(array, percentile) {
                        array.sort((a, b) => a - b)
                        const index = percentile * (array.length - 1)
                        const lower = Math.floor(index)
                        const remainder = index - lower
                        if (array[lower + 1] !== undefined) {
                            return array[lower] + remainder * (array[lower + 1] - array[lower])
                        } else {
                            return array[lower]
                        }
                    }

                    // Extract the values and sort
                    let values = dataArray.map((item) => parseFloat(item[1])).sort((a, b) => a - b)

                    // Compute Q1, Q3, and IQR
                    let Q1 = quartile(values, 0.25)
                    let Q3 = quartile(values, 0.75)
                    let IQR = Q3 - Q1

                    // Define the bounds for outliers
                    let lowerBound = Q1 - 1.5 * IQR
                    let upperBound = Q3 + 1.5 * IQR

                    // Filters our data for non-outliers
                    t.usageByHour = dataArray.filter((item) => {
                        let v = parseFloat(item[1])
                        return v >= lowerBound && v <= upperBound
                    })

                    t.dataByHour = {
                        usage: this.usageByHour,
                        temperature: this.tempByHour
                    }
                })
                .catch(() => {
                    // console.error(err)
                    // console.error('Error fetching hourly usage data:', err)
                })
        },
        // GET TEMPERATURE BY HOUR
        async getTempByHour() {
            let t = this;
            let url = `${process.env.VUE_APP_API_URL}publicV2/timeseries/range`;
            let timeseries = `ts:${t.site.siteId}:temperature:total`;

            // Don't get data before site start date
            if (this.startDateUnix < this.$dayjs(this.site.startDateTime)) {
                this.startDateUnix = this.$dayjs(this.site.startDateTime)
                    .tz('Europe/London')
                    .startOf('day')
                    .valueOf();
            }

            await t.$axios
                .post(url, {
                    orgId: t.site.orgId,
                    timeseries: timeseries,
                    reverseRange: false,
                    aggregation: 'avg',
                    bucketSize: 3600000,
                    startTimestamp: t.startDateUnix,
                    endTimestamp: t.endDateUnix,
                    timestampFormat: 'valueOf',
                    outputFormat: 'array'
                })
                .then((res) => {
                    //console.log("📥 Raw res.data.data:", res.data.data);

                    let data = res.data.data;
                    t.tempByHour = data.map((item) => [item[0], parseInt(item[1])]);

                    //console.log("📊 Processed t.tempByHour:", t.tempByHour);
                })
                .catch(() => {
                    // console.error("❌ Error fetching temperature data:", err);
                });
        },
        // CALCULATE ESTIMATED ENERGY USAGE
        async calculateEstimatedEnergyUsage(temperatureData, energyUsageMap) {
            return new Promise((resolve, reject) => {
                try {
                    // console.log(`[${this.site?.siteId}] 🔢 calculateEstimatedEnergyUsage - START`)
                    // console.log(`[${this.site?.siteId}] Temperature data points:`, temperatureData?.length)
                    // console.log(`[${this.site?.siteId}] Energy usage map keys:`, Object.keys(energyUsageMap || {}).slice(0, 10))
                    // console.log(`[${this.site?.siteId}] Energy usage map sample values:`, Object.entries(energyUsageMap || {}).slice(0, 3))
                    // console.log(`[${this.site?.siteId}] Energy usage map type:`, typeof energyUsageMap)
                    
                    if (!energyUsageMap || Object.keys(energyUsageMap).length === 0) {
                        // console.warn(`[${this.site?.siteId}] ⚠️ energyUsageMap is empty or undefined!`)
                        resolve(0)
                        return
                    }
                    
                    // Calculate fallback energy (average of all temperature values)
                    const energyValues = Object.values(energyUsageMap).filter(v => v !== null && v !== undefined && v !== '')
                    const fallbackEnergy = energyValues.length > 0 
                        ? energyValues.reduce((sum, val) => sum + parseFloat(val), 0) / energyValues.length 
                        : 0
                    
                    // Calculate expected hours based on date range and day/night filter
                    const totalHours = Math.ceil((this.endDateUnix - this.startDateUnix) / 3600000)
                    let dayNightMultiplier = 1
                    if (this.day && !this.night) dayNightMultiplier = 0.5
                    if (!this.day && this.night) dayNightMultiplier = 0.5
                    const expectedHours = Math.floor(totalHours * dayNightMultiplier)
                    
                    // console.log(`[${this.site?.siteId}] Expected hours: ${expectedHours}, Actual data points: ${temperatureData?.length}`)
                    // console.log(`[${this.site?.siteId}] Fallback energy per hour: ${fallbackEnergy.toFixed(2)} kWh`)
                    
                    let totalEnergyUsage = 0
                    let processedHours = 0
                    
                    for (const entry of temperatureData) {
                        // Assuming the temperature is rounded to the nearest whole number for lookup
                        let temperature = Math.round(entry[1])
                        if (temperature < 0) {
                            temperature = 0
                        }
                        if (temperature > 19) {
                            temperature = 19
                        }
                        const energyUsage = energyUsageMap[temperature]
                        if (energyUsage !== undefined && energyUsage !== null && energyUsage !== '') {
                            totalEnergyUsage += energyUsage
                            processedHours++
                        } else {
                            // Handle the case where there's no matching temperature in energyUsageMap
                            // console.warn(`[${this.site?.siteId}] No energy data for temperature: ${temperature}`)
                        }
                    }
                    
                    // Calculate missing hours and add fallback energy
                    const missingHours = expectedHours - processedHours
                    if (missingHours > 0) {
                        const fallbackTotal = missingHours * fallbackEnergy
                        totalEnergyUsage += fallbackTotal
                        
                        // const dataCompleteness = ((processedHours / expectedHours) * 100).toFixed(1)
                        // console.log(`[${this.site?.siteId}] Missing ${missingHours} hours (${(100 - dataCompleteness).toFixed(1)}% gap)`)
                        // console.log(`[${this.site?.siteId}] Adding fallback energy: ${fallbackTotal.toFixed(2)} kWh`)
                        // console.log(`[${this.site?.siteId}] Data completeness: ${dataCompleteness}%`)
                    }
                    
                    // console.log(`[${this.site?.siteId}] 🔢 Total estimated energy:`, totalEnergyUsage.toFixed(2))
                    resolve(totalEnergyUsage)
                } catch (error) {
                    // console.error(`[${this.site?.siteId}] ❌ Error in calculateEstimatedEnergyUsage:`, error)
                    reject(error)
                }
            })
        },
        // GET USAGE BY BOX
        getUsageByBox(box) {
            if (box) {
                let boxData = this.computedUsageByBoxLetter.find((x) => x.name === box)
                if (boxData) {
                    return parseFloat(boxData.value.toFixed(2))
                } else {
                    return 0
                }
            }
        },
        // EXPORT LOAD CSV
        exportLoadCSV() {
            const data = this.loadSeries.map((row) => ({
                timestamp: this.$dayjs(row[0], 'x').format('DD-MMM-YYYY HH:mm'),
                watts: row[1]
            }))
            let csv = this.$papa.unparse(data)
            this.$papa.download(csv, 'Load_Over_Time')
        },
        // EXPORT LOAD AND TEMP CSV
        exportLoadandTempCSV() {
            const data = this.dataByHour.usage.map((row, index) => ({
                timestamp: this.$dayjs(row[0], 'x').format('DD-MMM-YYYY HH:mm'),
                usage: Math.floor(row[1]),
                temperature: this.dataByHour.temperature[index][1]
            }))
            let csv = this.$papa.unparse(data)
            this.$papa.download(csv, 'Hourly_Usage_And_Temperature')
        },
        // EXPORT USAGE CSV
        exportUsageCSV() {
            const data = this.usageTimeseries.map((row) => ({
                timestamp: this.$dayjs(row[0], 'x').format('DD-MMM-YYYY HH:mm'),
                cumulative_usage: row[1]
            }))
            let csv = this.$papa.unparse(data)
            this.$papa.download(csv, 'Cumlative_Usage_Over_Time')
        },
        /**
         * Set the date field, based on the range passed in
         * @param range
         */
        // SET DATE FIELD
        setDateField(range) {
            if (range === 'week') {
                this.startDate = this.$dayjs().subtract(8, 'day').format('YYYY-MM-DD')
                this.endDate = this.$dayjs().subtract(1, 'day').format('YYYY-MM-DD')
            } else if (range === 'month') {
                this.startDate = this.$dayjs().subtract(1, 'month').format('YYYY-MM-DD')
                this.endDate = this.$dayjs().format('YYYY-MM-DD')
            } else if (range === 'quarter') {
                this.startDate = this.$dayjs().subtract(1, 'day').subtract(3, 'month').format('YYYY-MM-DD')
                this.endDate = this.$dayjs().format('YYYY-MM-DD')
            } else if (range === 'year') {
                this.startDate = this.$dayjs().subtract(1, 'day').subtract(1, 'year').format('YYYY-MM-DD')
                this.endDate = this.$dayjs().format('YYYY-MM-DD')
            }
            this.calculate()
        }
    },
    watch: {
        // WATCH loadCircuit then getLoadOverTime
        loadCircuit: {
            handler: function () {
                this.getLoadOverTime()
            },
            deep: true
        },
        // WATCH usageCircuit then getUsageOverTime
        usageCircuit: {
            handler: function () {
                this.getUsageOverTime()
            },
            deep: true
        },
        // WATCH usageRangeCircuit then getUsageRange
        usageRangeCircuit: {
            handler: function () {
                this.getUsageRange()
            },
            deep: true
        },
        // WATCH hourlyUsageCircuit then getUsageByHour
        hourlyUsageCircuit: {
            handler: function () {
                this.getUsageByHour()
            },
            deep: true
        },
        // WATCH selectedSiteCircuits then update dropdown selection
        selectedSiteCircuits: {
            handler: function () {
                //if cleared, set to all
                if (this.selectedSiteCircuits?.length === 0) {
                    this.selectedSiteCircuits = ['All']
                }

                //if it has more than just ['all'], remove 'all'
                if (this.selectedSiteCircuits.includes('All') && this.selectedSiteCircuits.length > 1) {
                    this.selectedSiteCircuits = this.selectedSiteCircuits.filter((circuit) => circuit !== 'All')
                }

                //changed calculate to run on button click like the rest, to avoid multiple calls
                // this.calculate()
            },
            deep: true
        }
    },
    async created() {
        this.loading = true
        await this.getSite()
        await this.getCircuits()
        await this.calculate()
        this.loading = false
    }
}
</script>
